import { useRef, useState } from "react";

import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import {
  UploadData,
  UploadFileData,
  UploadFilesWithOverwriteConfirmArgs,
} from "../types/upload-types";

import { UploadFilesFunc } from "./use-file-uploading-provider";

type Overwrite = {
  directory: string;
  uploadIndex: number;
  currentFileIndex: number;
  name: string;
  dontOverwrite?: boolean;
};

export interface OverwriteState extends UploadFilesWithOverwriteConfirmArgs {
  overwrites: Overwrite[];
}

const findOverwrites = (uploads: UploadFilesWithOverwriteConfirmArgs["uploads"]): Overwrite[] =>
  uploads.flatMap(({ files, currentFiles, directory }) =>
    currentFiles.flatMap(({ id }, currentFileIndex) => {
      const currentFileName = getNameFromFileId(id);
      const uploadIndex = files.findIndex(({ file }) => file.name === currentFileName);

      if (uploadIndex === -1) {
        return [];
      }

      return [
        {
          directory,
          uploadIndex,
          currentFileIndex,
          name: files[uploadIndex].file.name,
        },
      ];
    })
  );

const applyFileRename = (upload: UploadFileData, name: string): UploadFileData => {
  // No rename is needed when the selected name is unchanged.
  if (name === upload.file.name) {
    return upload;
  }

  // Keep the upload data intact while replacing the File object with one that has the new name.
  return {
    ...upload,
    file: new File([upload.file], name, { type: upload.file.type }),
  };
};

const applyOverwriteDecisions = (
  uploads: UploadFilesWithOverwriteConfirmArgs["uploads"],
  overwrites: Overwrite[]
): UploadData[] =>
  // Apply each conflict decision to its directory, omitting files marked "do not overwrite" and
  // renaming files that the user chose to upload under a different name.
  uploads.map(({ currentFiles: _, files, directory }) => {
    const directoryOverwrites = overwrites.filter((overwrite) => overwrite.directory === directory);

    if (directoryOverwrites.length === 0) {
      return { directory, files };
    }

    const updatedFiles = files.flatMap((upload, uploadIndex) => {
      const overwrite = directoryOverwrites.find((item) => item.uploadIndex === uploadIndex);

      if (overwrite?.dontOverwrite) {
        return [];
      }

      return [applyFileRename(upload, overwrite?.name ?? upload.file.name)];
    });

    return { directory, files: updatedFiles };
  });

export function useFileUploadingOverwriteModal(uploadFiles: UploadFilesFunc) {
  const [overwriteState, setOverwriteState] = useState<OverwriteState | null>(null);
  const pendingCallbackRef = useRef<(() => void) | undefined>();

  const uploadFilesWithOverwriteConfirm = (
    { projectId, uploads, withoutProgressDialog }: UploadFilesWithOverwriteConfirmArgs,
    onComplete?: () => void
  ) => {
    // Store the callback to call after overwrite confirmation
    pendingCallbackRef.current = onComplete;

    // Check for overwrites by comparing upload file names with current file names in the same directory
    const overwrites = findOverwrites(uploads);

    // If there are no overwrites, proceed with the upload immediately
    if (!overwrites.length) {
      const result = uploadFiles({ projectId, uploads, withoutProgressDialog }, onComplete);
      pendingCallbackRef.current = undefined;
      return result;
    }

    // If there are overwrites, open the modal to ask the user for confirmation
    setOverwriteState({
      projectId,
      uploads,
      withoutProgressDialog,
      overwrites,
    });
  };

  /** Update the modal's decisions, or pass null to close the modal without uploading. */
  const setOverwrites = (overwrites: OverwriteState["overwrites"] | null) => {
    if (!overwriteState) return null;
    setOverwriteState(overwrites ? { ...overwriteState, overwrites } : null);
  };

  /** Apply the final decisions, start the upload, and clear the pending overwrite state. */
  const startOverwritesUpload = (overwrites: OverwriteState["overwrites"]) => {
    if (!overwriteState) return null;

    const updatedUploads = applyOverwriteDecisions(overwriteState.uploads, overwrites);

    uploadFiles({ ...overwriteState, uploads: updatedUploads }, pendingCallbackRef.current);

    setOverwriteState(null);
    pendingCallbackRef.current = undefined;
  };
  return {
    uploadFilesWithOverwriteConfirm,
    overwriteState,
    setOverwrites,
    startOverwritesUpload,
  };
}
